import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartPie } from "lucide-react";
import type { ChartConfig } from "@/components/ui/chart";
import { AnalyticsDonutChartArea } from "../shared/AnalyticsDonutChartArea";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { REVENUE_LEGEND_PERCENT_COLOR } from "../revenue/constants";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import type { StarDistributionSegment } from "./types";

interface ReviewsDistributionCardProps {
  segments: StarDistributionSegment[];
  totalReviews: number;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const ReviewsDistributionCard: React.FC<
  ReviewsDistributionCardProps
> = ({ segments, totalReviews, onExport, exportDisabled }) => {
  const { t } = useTranslation();
  const total = useMemo(
    () => segments.reduce((sum, segment) => sum + segment.value, 0),
    [segments],
  );

  const chartConfig = useMemo(
    () =>
      segments.reduce<ChartConfig>((config, segment) => {
        config[segment.key] = {
          label: segment.name,
          color: segment.fill,
        };
        return config;
      }, {}),
    [segments],
  );

  const totalReviewsLabel = t(
    "analytics.reviews.totalReviews",
    "Total reviews",
  );

  return (
    <RevenueChartCard
      title={t("analytics.reviews.distribution", "Distribution")}
      icon={<ChartPie />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      className="min-h-0 flex-1"
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex flex-col items-center gap-3">
        <AnalyticsDonutChartArea
          segments={segments}
          total={total}
          chartConfig={chartConfig}
          centerPrimary={totalReviews}
          centerSecondary={totalReviewsLabel}
        />

        <ul className="flex w-full max-w-[240px] flex-col gap-3">
          {segments.map((segment) => {
            const percent =
              total > 0 ? Math.round((segment.value / total) * 100) : 0;

            return (
              <li
                key={segment.key}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: segment.fill }}
                  />
                  <span className="shrink-0 text-sm leading-normal text-[#171717]">
                    {segment.name}
                  </span>
                </div>
                <span
                  className="shrink-0 text-sm font-normal"
                  style={{ color: REVENUE_LEGEND_PERCENT_COLOR }}
                >
                  {percent}%
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </RevenueChartCard>
  );
};
